import React, { useState } from "react";

const changelogData = [
  {
    version: "2.2.0",
    date: "2026-04-01",
    changes: [
      "Introduced Vapor Relay Network 2.0 with improved latency and reliability",
      "Added support for scheduling disappearing messages by time of day",
      "New compact chat bubble mode for high-density conversations",
      "Fixed notification badge not clearing on certain Android devices",
    ],
  },
  {
    version: "2.1.5",
    date: "2026-03-15",
    changes: [
      "Hotfix for crash when opening image attachments on iOS 19",
      "Improved memory usage during long group voice calls",
    ],
  },
  {
    version: "2.1.0",
    date: "2026-02-20",
    changes: [
      "Voice messages now support up to 10 minutes with end-to-end encryption",
      "Added message search within encrypted conversations",
      "Redesigned settings page with categorized sections",
      "Fixed issue where onion routing path selection failed in certain regions",
    ],
  },
  {
    version: "2.0.2",
    date: "2026-01-28",
    changes: [
      "Critical fix for message delivery delays on Windows desktop client",
      "Resolved sync conflict when switching between mobile and desktop",
    ],
  },
  {
    version: "2.0.0",
    date: "2026-01-10",
    changes: [
      "Major release: New UI redesign with modern, clean interface",
      "Introduced multi-account support — switch between identities seamlessly",
      "New onion routing engine with 40% faster message delivery",
      "Added QR code sharing for Account ID",
      "Group admin controls: mute members, set posting permissions",
      "End-to-end encrypted file sharing up to 100MB",
      "Dark mode improvements with OLED-optimized true black theme",
    ],
  },
  {
    version: "1.5.3",
    date: "2025-11-22",
    changes: [
      "Fixed rare crash when receiving messages in archived conversations",
      "Improved Tor bridge connection stability",
    ],
  },
  {
    version: "1.5.0",
    date: "2025-10-08",
    changes: [
      "Added read receipts (optional, disabled by default for privacy)",
      "Introduced custom notification sounds per conversation",
      "New emoji reactions for messages",
      "Optimized database for faster app startup on older devices",
    ],
  },
  {
    version: "1.4.5",
    date: "2025-08-14",
    changes: [
      "Fixed media gallery not loading in group chats",
      "Improved keyboard handling on Android tablets",
      "Added support for animated stickers",
    ],
  },
  {
    version: "1.4.2",
    date: "2025-06-30",
    changes: [
      "Security patch: Updated cryptographic library to latest version",
      "Fixed disappearing messages timer reset bug",
    ],
  },
  {
    version: "1.4.0",
    date: "2025-04-25",
    changes: [
      "Added 'Self-Destructing Messages' (customizable timer)",
      "Upgraded multi-device synchronization to minimize data conflicts",
      "Enhanced Tor relay compatibility for improved anonymity",
    ],
  },
  {
    version: "1.3.5",
    date: "2025-03-18",
    changes: [
      "Critical fix addressing message loss on Android from v1.3.0 regression",
    ],
  },
  {
    version: "1.3.2",
    date: "2025-03-01",
    changes: [
      "Battery optimization through reduced background sync frequency",
      "Introduced message recall capability (5-minute undo window)",
      "Resolved rare group creation failure",
    ],
  },
  {
    version: "1.3.0",
    date: "2025-01-22",
    changes: [
      "Prevented crash during member removal handling",
      "Updated AGP and key dependencies",
      "Fixed empty home screen state display",
      "Corrected message send/receive info",
      "Adjusted reply icon positioning",
      "Added error handling for swarm operations",
      "Removed group invites when rejecting membership",
      "General bug fixes and improvements",
    ],
  },
  {
    version: "1.2.25",
    date: "2024-12-05",
    changes: [
      "Fixed phone call initiation from lock screen",
      "Resolved group polling issues during invitation acceptance",
    ],
  },
  {
    version: "1.2.5",
    date: "2024-11-13",
    changes: [
      "Updated minimum version banner color",
    ],
  },
  {
    version: "1.2.0",
    date: "2024-11-06",
    changes: [
      "Consolidated member state management",
      "Fixed v2 group exit functionality",
      "Added camera screen padding to prevent system bar overlap",
    ],
  },
];

const PAGE_SIZE = 6;

const Changelog = () => {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(changelogData.length / PAGE_SIZE);
  const pageData = changelogData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <section className="changelog changelog-page">
      <div className="container">
        <h2 className="changelog-title">Changelog</h2>
        <p className="changelog-subtitle">Track every update to Vapor.</p>

        <div className="changelog-timeline">
          {pageData.map((release, i) => (
            <div className="changelog-entry" key={`${page}-${i}`}>
              <div className="changelog-marker">
                <span className="changelog-dot" />
                {i < pageData.length - 1 && <span className="changelog-line" />}
              </div>
              <div className="changelog-card">
                <div className="changelog-header">
                  <span className="changelog-version">v{release.version}</span>
                  <span className="changelog-date">{release.date}</span>
                </div>
                <ul className="changelog-list">
                  {release.changes.map((change, j) => (
                    <li key={j}>{change}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* 分頁 */}
        {totalPages > 1 && (
          <div className="changelog-pagination">
            <button
              className="changelog-page-btn"
              disabled={page === 0}
              onClick={() => { setPage(page - 1); window.scrollTo(0, 0); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              Prev
            </button>
            <div className="changelog-page-nums">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`changelog-page-num ${i === page ? "active" : ""}`}
                  onClick={() => { setPage(i); window.scrollTo(0, 0); }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              className="changelog-page-btn"
              disabled={page === totalPages - 1}
              onClick={() => { setPage(page + 1); window.scrollTo(0, 0); }}
            >
              Next
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Changelog;
